import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useDateLocale } from '../../../utils/dateLocale';
import { catHex } from './meta';
import { Icon, Ticket } from '../../ui/Icon';
import { expenseReservation } from '../../../utils/reservations';
import type { Expense } from '../../../types';

export const LEDGER_COLS = 'grid-cols-[88px_1fr_120px_100px_72px_96px]';

interface Props {
  expense: Expense;
  onSelect: (e: Expense) => void;
}

/**
 * One expense, in two layouts.
 *
 * The table needs ~600px of fixed column width, and the card that wraps it
 * clips its overflow — on a phone that put the amount off-screen with no way to
 * reach it. So below `md` the same fields are stacked instead, matching
 * gromly-mobile's `LedgerRow`: date block, then description over a meta line,
 * then the amount.
 *
 * The whole row opens the expense in edit mode (deleting lives inside that
 * form): per-row pencil/trash icons meant a 28px column of targets that were
 * invisible until hover on desktop and cramped on a phone.
 */
export function LedgerRow({ expense, onSelect }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const payer    = expense.payer?.displayName || expense.payer?.username || '—';
  const catColor = catHex(expense.category);
  const catLabel = t(`expenses.categories.${expense.category}` as Parameters<typeof t>[0]);
  const date     = expense.date ? new Date(expense.date) : null;
  const splitN   = expense.splits.length;
  const linked   = !!expenseReservation(expense) && (
    <Icon as={Ticket} size={12} className="inline shrink-0 mr-1 -mt-0.5 text-slate-400" aria-label={t('expenses.table.linkedReservation')} />
  );
  const converted = expense.originalCurrency
    && expense.originalCurrency !== expense.currency
    && expense.originalAmount != null;

  const original = converted && (
    <p className="font-mono text-[10px] text-slate-400 tabular-nums truncate">
      {Number(expense.originalAmount).toFixed(2)} {expense.originalCurrency}
    </p>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(expense)}
      onKeyDown={e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onSelect(expense);
      }}
      aria-label={t('expenses.table.editRow', { description: expense.description })}
      className="cursor-pointer hover:bg-slate-50/60 dark:hover:bg-slate-800/30 focus-visible:outline-none focus-visible:bg-slate-50/60 dark:focus-visible:bg-slate-800/30 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600 transition-colors"
    >
      {/* Phone */}
      <div className="md:hidden flex items-start gap-3 px-4 py-3">
        <div className="w-9 shrink-0 text-center pt-0.5">
          <p className="font-serif font-bold text-lg leading-none text-slate-900 dark:text-slate-100">
            {date ? format(date, 'd', { locale: dateLocale }) : '—'}
          </p>
          <p className="font-mono text-[9px] tracking-[0.12em] uppercase text-slate-400 mt-0.5">
            {date ? format(date, 'MMM', { locale: dateLocale }) : ''}
          </p>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
            {linked}{expense.description}
          </p>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1 font-mono text-[11px] text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: catColor }} />
            <span>{catLabel}</span>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <span className="truncate max-w-[9rem]">{payer}</span>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <span>{splitN > 0 ? `${splitN}p` : '—'}</span>
          </div>
          {expense.notes && (
            <p className="font-mono text-[11px] text-slate-400 truncate mt-0.5">{expense.notes}</p>
          )}
        </div>

        <div className="shrink-0 flex flex-col items-end gap-0.5">
          <span className="font-mono font-bold text-base text-slate-900 dark:text-slate-100 tabular-nums leading-none">
            {Number(expense.amount).toFixed(2)}
          </span>
          {original}
        </div>
      </div>

      {/* Table, from `md` up */}
      <div className={`hidden md:grid ${LEDGER_COLS} gap-3 px-4 py-3 items-center`}>
        <span className="font-mono text-xs text-slate-400 truncate">
          {date ? format(date, 'dd MMM', { locale: dateLocale }) : '—'}
        </span>

        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{linked}{expense.description}</p>
          {expense.notes && (
            <p className="font-mono text-[11px] text-slate-400 truncate mt-0.5">{expense.notes}</p>
          )}
        </div>

        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: catColor }} />
          <span className="font-mono text-xs text-slate-500 dark:text-slate-400 truncate">{catLabel}</span>
        </div>

        <span className="font-mono text-xs text-slate-500 dark:text-slate-400 truncate">{payer}</span>

        <span className="font-mono text-xs text-slate-400">{splitN > 0 ? `${splitN}p` : '—'}</span>

        <div className="text-right min-w-0">
          <span className="font-mono font-bold text-base text-slate-900 dark:text-slate-100 tabular-nums">
            {Number(expense.amount).toFixed(2)}
          </span>
          {original}
        </div>
      </div>
    </div>
  );
}
